import firebase from "firebase";
import { Trans, useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useState } from "react";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { Label } from "@/features/common/components/tailwind/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/features/common/components/tailwind/RadioGroup";
import { NewPaymentForm } from "@/features/payment/components/NewPaymentForm";
import { CardPayment } from "@/features/subscription/components/CardPayment";
import { JoinSubscriptionPaymentViewPlaceholder } from "@/features/subscription/components/joinSubscriptionDialog/JoinSubscriptionPaymentViewPlaceholder";
import { usePurchaseExpiredContentWithStripe } from "@/features/subscription/hooks/usePurchaseExpiredContentWithStripe";
import { useSubscribeWithStripe } from "@/features/subscription/hooks/useSubscribeWithStripe";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  PaymentMethodProviderEnum,
  PaymentMethodStatusEnum,
  useUserPaymentMethodsQuery,
} from "@/generated/generated-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

import { Link } from "../JoinSubscriptionPaymentView";
import { JoinSubscriptionConfirmPaymentButton } from "./JoinSubscriptionConfirmPaymentButton";
import { PurchaseExpiredContentButton } from "./PurchaseExpiredContentButton";

const StripeProvider = dynamic(
  () => import("@/features/stripe/StripeProvider"),
  { ssr: false },
);

type JoinSubscriptionPaymentViewProps = {
  subscriptionPlanId: string;
  onCancel: () => void;
  isUnlockExpiredPost: boolean;
  postId: string;
};

const {
  paymentMethod: { limit },
} = getPublicConfig();

export const JoinSubscriptionByStripeView: React.FC<JoinSubscriptionPaymentViewProps> =
  ({ subscriptionPlanId, postId, onCancel, isUnlockExpiredPost }) => {
    const { t } = useTranslation(["subscriptionPlan", "common"]);
    const { user } = useUserContext();
    const [userPaymentMethodId, setUserPaymentMethodId] = useState(null);

    const purchaseUnlockContentWithStripe = usePurchaseExpiredContentWithStripe(
      {
        subscriptionPlanId,
        postId,
      },
    );
    const subscribeWithNewPaymentMethod =
      useSubscribeWithStripe(subscriptionPlanId);
    const newPaymentFormAction = isUnlockExpiredPost
      ? purchaseUnlockContentWithStripe
      : subscribeWithNewPaymentMethod;

    const { data, loading, error } = useUserPaymentMethodsQuery({
      variables: {
        userId: user._id,
        paymentMethodProvider: PaymentMethodProviderEnum.Stripe2,
        paymentMethodStatus: [
          PaymentMethodStatusEnum.Default,
          PaymentMethodStatusEnum.Active,
        ],
      },
      skip: !user,
      ssr: false,
      onCompleted(data) {
        if (data?.userPaymentMethods?.length > 0) {
          setUserPaymentMethodId(data.userPaymentMethods[0]._id);
        }
      },
    });
    const canAddPaymentMethod = data?.userPaymentMethods?.length < limit;

    const onClickPaymentMethod = (value: string) => {
      firebase
        .analytics()
        .logEvent(AnalyticsEventEnum.PaymentConfirmSelectPayment, {
          isNew: value === "",
        });
      setUserPaymentMethodId(value);
    };

    const getPaymentMethodList = () => {
      if (!data || loading) return null;
      return data.userPaymentMethods.map((paymentMethods) => {
        const { _id, paymentMethodDetail, paymentMethodStatus } =
          paymentMethods;

        if (!paymentMethodDetail?.card) return null;
        const { lastFourDigits, brand, expYear, expMonth } =
          paymentMethodDetail.card;

        return (
          <CardPayment
            key={_id}
            userPaymentMethodId={_id}
            lastFourDigits={lastFourDigits}
            brand={brand}
            expMonth={expMonth}
            expYear={expYear}
          />
        );
      });
    };

    if (loading) return <JoinSubscriptionPaymentViewPlaceholder />;
    if (!loading && !data) {
      return (
        <div className="w-full">
          <NewPaymentForm
            onCancel={onCancel}
            onSubmit={newPaymentFormAction}
            addingPaymentMethodBeforeSubscribe={true}
          />
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col w-full mt-4 space-y-2">
          <RadioGroup
            onValueChange={onClickPaymentMethod}
            className="mt-4"
            defaultValue={data.userPaymentMethods[0]?._id}
          >
            {getPaymentMethodList()}
            {/* new payment form */}
            {canAddPaymentMethod && (
              <>
                <div className="flex items-center">
                  <RadioGroupItem
                    value={undefined}
                    id="newPaymentMethod"
                    aria-label="new card form"
                  />
                  <Label htmlFor="newPaymentMethod" className="touchable">
                    {t("common:paymentForm.new")}
                  </Label>
                </div>

                {!userPaymentMethodId && (
                  <div className="w-full">
                    <NewPaymentForm
                      onCancel={onCancel}
                      onSubmit={newPaymentFormAction}
                      confirmLabel={t("subscriptionPlan:pay")}
                      addingPaymentMethodBeforeSubscribe={true}
                    />
                  </div>
                )}
              </>
            )}
          </RadioGroup>
          <div>
            <span className="text-sm font-light text-gray-900">
              <Trans
                i18nKey="common:paymentForm.agree"
                components={{
                  privacy: (
                    <Link
                      href={RouteName.PrivacyPolicy}
                      label={t("common:terms.privacyPolicy")}
                    />
                  ),
                  termsOfUse: (
                    <Link
                      href={RouteName.TermsOfUse}
                      label={t("common:terms.termsOfUse")}
                    />
                  ),
                }}
              />
            </span>
          </div>
        </div>

        {/* confirm and previous button */}
        {!!userPaymentMethodId && (
          <div className="flex justify-end w-full mt-4">
            {onCancel && (
              <OutlinedButton
                variant="secondary"
                buttonProps={{ onClick: onCancel }}
              >
                {t("common:cancel")}
              </OutlinedButton>
            )}

            <div className="ml-4">
              <StripeProvider>
                {isUnlockExpiredPost ? (
                  <PurchaseExpiredContentButton
                    postId={postId}
                    subscriptionPlanId={subscriptionPlanId}
                    userPaymentMethodId={userPaymentMethodId}
                    onCompleted={onCancel}
                  />
                ) : (
                  <JoinSubscriptionConfirmPaymentButton
                    userPaymentMethodId={userPaymentMethodId}
                    subscriptionPlanId={subscriptionPlanId}
                    onCompleted={onCancel}
                  />
                )}
              </StripeProvider>
            </div>
          </div>
        )}
      </>
    );
  };
